/*
 * Created on Wed Oct 11 2023 4:46:23 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export const usePaymentAccess = () => {
  // Get company data from Redux store
  const companyState = useSelector((state) => state?.companyReducer?.company) || {};
  
  // Extract allowTransaction flag from company account
  const allowTransaction = companyState?.account?.allowTransaction || false;
  
  // Get contact person details from company data
  const contactPerson = companyState?.contactPerson || {};
  
  // Format contact details
  const contactEmail = contactPerson?.email || '';
  const contactPhone = contactPerson?.phoneNumber || '';
  const countryCode = contactPerson?.countryCode || '1';
  const contactName = contactPerson?.firstName && contactPerson?.lastName 
    ? `${contactPerson.firstName} ${contactPerson.lastName}`
    : 'Sales Team';
  
  const formattedPhone = contactPhone ? `+${countryCode} ${contactPhone}` : '';

  const getButtonConfig = useCallback((defaultText, restrictedText = 'Explore Spaces', restrictedAction) => {
    if (allowTransaction) {
      return {
        text: defaultText,
        action: null,
        isAllowed: true,
        icon: null
      };
    }

    return {
      text: restrictedText,
      action: restrictedAction || (() => {
        if (contactEmail) {
          window.location.href = `mailto:${contactEmail}?subject=Payment%20Activation%20Inquiry`;
        }
      }),
      isAllowed: false,
      icon: 'mail'
    };
  }, [allowTransaction, contactEmail]);

  const checkPaymentAccess = useCallback((callback, fallback) => {
    if (allowTransaction) {
      callback();
    } else if (fallback) {
      fallback();
    }
  }, [allowTransaction]);

  const getContactMethods = useCallback(() => {
    const methods = [];
    
    if (contactEmail) {
      methods.push({
        type: 'email',
        value: contactEmail,
        href: `mailto:${contactEmail}?subject=Payment%20Activation%20Inquiry`,
        icon: 'mail'
      });
    }
    
    if (contactPhone) {
      methods.push({
        type: 'phone',
        value: formattedPhone,
        href: `tel:+${countryCode}${contactPhone}`,
        icon: 'phone'
      });
    }
    
    return methods;
  }, [contactEmail, contactPhone, formattedPhone, countryCode]);

  return {
    getButtonConfig,
    checkPaymentAccess,
    isPaymentAllowed: allowTransaction,
    contactPerson,
    contactEmail,
    contactPhone: formattedPhone,
    contactName,
    getContactMethods,
    companyData: companyState
  };
};